import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import IconChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_INTENTS, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {
  Drawer,
  DRAWER_PLACEMENTS,
  DrawerBackdrop,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerPopup,
  DrawerPortal,
  DrawerTitle,
  DrawerViewport
} from '@adv-mt/ui/drawer'
import {Body1} from '@adv-mt/ui/typography'

import AppsFlyerSmartScriptLoader from './appsflyerSmartScriptLoader.js'
import {getSmartScriptUrl} from './helpers.js'
import {useCooldownAppSuggestion} from './useCooldownAppSuggestion.js'

const BASE_CLASS = 'mt-DrawerAppSuggestion'
const APP_LOGO_SIZE = 40

const BROWSER_ICON_PATH = 'common/icons/generic-browser.svg'

const OPTION_BUTTON_PROPS = {
  className: `${BASE_CLASS}-option`,
  fullWidth: true,
  intent: BUTTON_INTENTS.NEUTRAL,
  size: BUTTON_SIZES.LARGE,
  variant: BUTTON_VARIANTS.FLAT
}

const DrawerAppSuggestion = ({onKeepBrowserNavigation, onOpenAppClick, pageName, campaignSuffix, trackingOrigin}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const {COOLDOWN_COOKIE_NAME, COOLDOWN_HOURS, FALLBACK_URL, ONE_LINK_URL} = config.get('appSuggestion')
  const {HAS_APP_SUGGESTION_DRAWER_ENABLED} = config.get('features')

  const {
    HIT_INFORMATIONS: {
      APP_SUGGESTION_DRAWER_CLOSE_ACTION: closeAction,
      APP_SUGGESTION_DRAWER_INSTALL_ACTION: installAction,
      APP_SUGGESTION_DRAWER_POSITION_BOTTOM: bottomPosition
    },
    TRACKING_EVENT_NAMES: {
      APP_SUGGESTION_DRAWER_CLICKED: clickedEventName,
      APP_SUGGESTION_DRAWER_VIEWED: viewedEventName
    }
  } = config.get('tracking')

  const [isSmartScriptLoaded, setSmartScriptLoaded] = useState(false)
  const [targetUrl, setTargetUrl] = useState(FALLBACK_URL)

  const {track: analyticsTrack} = useAnalyticsTrack()

  const {allowRenderBasedOnCooldown, setCooldownPeriod, setAllowRenderBasedOnCooldown} = useCooldownAppSuggestion({
    cookieName: COOLDOWN_COOKIE_NAME
  })

  const showAppSuggestion = isMobile && HAS_APP_SUGGESTION_DRAWER_ENABLED && allowRenderBasedOnCooldown

  const trackAppSuggestionDrawerClicked = action =>
    analyticsTrack({
      event: clickedEventName,
      data: {hit_origin: trackingOrigin, hit_information: bottomPosition, hit_information2: action}
    })

  const handleOnClick = () => {
    onOpenAppClick?.()
    trackAppSuggestionDrawerClicked(installAction)
    window.location.href = targetUrl

    setCooldownPeriod(COOLDOWN_HOURS.AFTER_INSTALL_CLICK)
    setAllowRenderBasedOnCooldown(false)
  }

  const handleOnClose = () => {
    trackAppSuggestionDrawerClicked(closeAction)
    setCooldownPeriod(COOLDOWN_HOURS.DEFAULT)
    setAllowRenderBasedOnCooldown(false)
  }

  const handleOnOpenChange = open => {
    if (!open) handleOnClose()
  }

  const handleOnLoadSmartScript = () => {
    setSmartScriptLoaded(true)
  }

  useEffect(() => {
    if (!showAppSuggestion) return

    analyticsTrack({event: viewedEventName, data: {hit_origin: trackingOrigin, hit_information: bottomPosition}})
  }, [showAppSuggestion]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showAppSuggestion || !isSmartScriptLoaded || !window.AF_SMART_SCRIPT) return

    const clickURL = getSmartScriptUrl({oneLinkURL: ONE_LINK_URL, pageName, campaignSuffix})?.clickURL
    if (!clickURL) return

    setTargetUrl(clickURL)
    const impressionTimeout = setTimeout(() => {
      window.AF_SMART_SCRIPT.fireImpressionsLink()
    }, 1000)

    return () => clearTimeout(impressionTimeout)
  }, [isSmartScriptLoaded, showAppSuggestion]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!showAppSuggestion) return null

  // Stamped on the popup even though <html> already carries data-theme: on <html> the `:root,:host` token
  // block also matches and, coming later in the file, beats `[data-theme=motor]` for --radius-sm/md/lg/xl.
  // An element with its own data-theme gets the motor radii (24px sheet corners instead of 10px).
  const designSystemTheme = config.get('DESIGN_SYSTEM_THEME')
  const appLogoUrl = `${config.get('URL_STATICS_IMAGES')}${config.get('LOGO_APPS_IOS_URL')}`
  const browserIconUrl = `${config.get('URL_STATICS_IMAGES')}${BROWSER_ICON_PATH}`

  return (
    <Drawer defaultOpen modal onOpenChange={handleOnOpenChange} placement={DRAWER_PLACEMENTS.BOTTOM}>
      {!isSmartScriptLoaded && <AppsFlyerSmartScriptLoader onLoad={handleOnLoadSmartScript} />}
      <DrawerPortal>
        <DrawerBackdrop data-theme={designSystemTheme} />
        <DrawerViewport>
          <DrawerPopup data-theme={designSystemTheme} placement={DRAWER_PLACEMENTS.BOTTOM}>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>{i18n.t('DRAWER_APP_SUGGESTION.TITLE')}</DrawerTitle>
              </DrawerHeader>
              <DrawerBody>
                <ul className={`${BASE_CLASS}-options`}>
                  <li className={`${BASE_CLASS}-optionItem`}>
                    <Button {...OPTION_BUTTON_PROPS} onClick={handleOnClick}>
                      <span className={`${BASE_CLASS}-optionContent`}>
                        <AtomImage
                          alt=""
                          className={`${BASE_CLASS}-optionLogo`}
                          height={APP_LOGO_SIZE}
                          src={appLogoUrl}
                          width={APP_LOGO_SIZE}
                        />
                        <Body1 className={`${BASE_CLASS}-optionLabel`}>
                          {i18n.t('DRAWER_APP_SUGGESTION.OPEN_APP', {siteName: config.get('SITE_NAME')})}
                        </Body1>
                        <IconChevronRight size={atomIconSizes.small} />
                      </span>
                    </Button>
                  </li>
                  <li className={`${BASE_CLASS}-optionItem`}>
                    <DrawerClose asChild>
                      <Button {...OPTION_BUTTON_PROPS} onClick={onKeepBrowserNavigation}>
                        <span className={`${BASE_CLASS}-optionContent`}>
                          <AtomImage
                            alt=""
                            className={`${BASE_CLASS}-optionLogo`}
                            height={APP_LOGO_SIZE}
                            src={browserIconUrl}
                            width={APP_LOGO_SIZE}
                          />
                          <Body1 className={`${BASE_CLASS}-optionLabel`}>
                            {i18n.t('DRAWER_APP_SUGGESTION.CONTINUE_WEB')}
                          </Body1>
                          <IconChevronRight size={atomIconSizes.small} />
                        </span>
                      </Button>
                    </DrawerClose>
                  </li>
                </ul>
              </DrawerBody>
            </DrawerContent>
          </DrawerPopup>
        </DrawerViewport>
      </DrawerPortal>
    </Drawer>
  )
}

DrawerAppSuggestion.displayName = 'DrawerAppSuggestion'

DrawerAppSuggestion.propTypes = {
  onKeepBrowserNavigation: PropTypes.func,
  onOpenAppClick: PropTypes.func,
  pageName: PropTypes.string.isRequired,
  campaignSuffix: PropTypes.string,
  trackingOrigin: PropTypes.string
}

export default DrawerAppSuggestion
